import { useCallback, useState } from 'react'
import type { ApiKey, ApiKeyCreated, CreateApiKeyPayload, UpdateApiKeyPayload } from 'types/api-keys'
import type { MutationResult, QueryResult } from 'utils/api'
import { qs } from 'utils/api'

import { useApiQuery } from '../useApiQuery'
import { useFetchApi } from '../useFetchApi'

export interface ApiKeysParams {
  skip?: number
  take?: number
  active?: boolean
}

export function useApiKeys(params: ApiKeysParams = {}): QueryResult<ApiKey[]> {
  return useApiQuery<ApiKey[]>(`/api/api-keys${qs(params)}`)
}

export function useCreateApiKey(): MutationResult<ApiKeyCreated, [CreateApiKeyPayload]> {
  const { fetchApi } = useFetchApi()
  const [loading, setLoading] = useState(false)
  const perform = useCallback(
    async (payload: CreateApiKeyPayload): Promise<ApiKeyCreated> => {
      setLoading(true)
      try {
        return await fetchApi<ApiKeyCreated>('/api/api-keys', {
          method: 'POST',
          body: JSON.stringify(payload),
        })
      } finally {
        setLoading(false)
      }
    },
    [fetchApi],
  )
  return { perform, loading }
}

export function useUpdateApiKey(): MutationResult<ApiKey, [string, UpdateApiKeyPayload]> {
  const { fetchApi } = useFetchApi()
  const [loading, setLoading] = useState(false)
  const perform = useCallback(
    async (id: string, payload: UpdateApiKeyPayload): Promise<ApiKey> => {
      setLoading(true)
      try {
        return await fetchApi<ApiKey>(`/api/api-keys/${id}`, {
          method: 'PUT',
          body: JSON.stringify(payload),
        })
      } finally {
        setLoading(false)
      }
    },
    [fetchApi],
  )
  return { perform, loading }
}

export function useRevokeApiKey(): MutationResult<ApiKey, [string]> {
  const { fetchApi } = useFetchApi()
  const [loading, setLoading] = useState(false)
  const perform = useCallback(
    async (id: string): Promise<ApiKey> => {
      setLoading(true)
      try {
        return await fetchApi<ApiKey>(`/api/api-keys/${id}`, { method: 'DELETE' })
      } finally {
        setLoading(false)
      }
    },
    [fetchApi],
  )
  return { perform, loading }
}
